import { apiPost } from "./api";
import { User } from "../store/authStore";

// Authentication request/response types
export interface LoginRequest {
  identifier: string; // username or email
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  birthdate: string;
  gender: "MAN" | "VROUW" | "TRANSGENDER" | "NON_BINAIR" | "GENDERFLUIDE" | "ANDERS" | "LIEVER_NIET";
  socialPreference:
    | "INTROVERT"
    | "VERLEGEN_NIET_INTROVERT"
    | "INTROVERT_NUCHTER"
    | "GEMIDDELD"
    | "BEETJE_EXTROVERT"
    | "SUPER_EXTROVERT";
  drugUsePreference:
    | "VOLLEDIG_NUCHTER_IEDEREEN"
    | "VOLLEDIG_NUCHTER_ANDEREN"
    | "ALCOHOL_MET_ANDEREN"
    | "ALCOHOL_ANDEREN_VRIJ"
    | "GA_HARD_ANDEREN_VRIJ"
    | "GA_HARD_MET_ANDEREN"
    | "FLEXIBEL_OMGEVING";
  musicalPreferences?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

class AuthService {
  /**
   * Login user with username/email and password
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return apiPost<AuthResponse>("/auth/login", credentials);
  }

  /**
   * Register new user
   */
  async register(userData: RegisterRequest): Promise<AuthResponse> {
    return apiPost<AuthResponse>("/auth/register", userData);
  }

  /**
   * Request password reset
   */
  async forgotPassword(
    request: ForgotPasswordRequest,
  ): Promise<{ message: string }> {
    return apiPost<{ message: string }>("/auth/forgot-password", request);
  }

  /**
   * Reset password with token
   */
  async resetPassword(
    request: ResetPasswordRequest,
  ): Promise<{ message: string }> {
    return apiPost<{ message: string }>("/auth/reset-password", request);
  }

  /**
   * Refresh access token
   */
  async refreshToken(request: RefreshTokenRequest): Promise<AuthResponse> {
    return apiPost<AuthResponse>("/auth/refresh", request);
  }

  /**
   * Logout user (invalidate tokens)
   */
  async logout(): Promise<void> {
    try {
      const { useAuthStore } = await import("../store/authStore");
      const { refreshToken } = useAuthStore.getState();
      
      if (refreshToken) {
        await apiPost("/auth/logout", { refreshToken });
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn("Logout API call failed:", error);
    }
  }

  /**
   * Check if username is available
   */
  async checkUsernameAvailability(username: string): Promise<boolean> {
    const response = await apiPost<{ available: boolean }>(
      `/auth/check-username`,
      { username },
    );
    return response.available;
  }

  /**
   * Check if email is available
   */
  async checkEmailAvailability(email: string): Promise<boolean> {
    const response = await apiPost<{ available: boolean }>(
      `/auth/check-email`,
      { email },
    );
    return response.available;
  }
}

export const authService = new AuthService();
